import React, { useEffect } from 'react';
import config from '../../../config/config';
import { FieldRenderer } from '../../inputs';
import Card from '../../Card';
import HierarchySelector from '../../HierarchySelector';
import { shouldDisableQuestion } from '../../../utils/incidentHelper';

const IncidentSection = ({
  name,
  reportSectionHeader,
  fields,
  answers,
  isIncidentBasics,
  isEditing,
  isCreating,
  activeIncident,
  handleSelectedAnswer,
  disableSection,
  alreadyHaveConfidential,
  missingRequired,
  autoConfidential
}) => {
  const groupId = answers?.locationId ?? activeIncident?.locationId;
  const projectId =
    answers && 'projectId' in answers
      ? answers.projectId
      : activeIncident?.projectId;

  const disabled = disableSection || (!isCreating && !isEditing);
  const witnessDisabled = !isCreating;
  const isCustomQuestions = reportSectionHeader === 'Custom Questions';
  const firstQuestion = fields.filter(
    f => f._id && f?._id === config.staticIds.confidentialReport
  )?.[0];

  const showConfidentialQuestion =
    !firstQuestion?.hide &&
    config.staticIds.confidentialReport === firstQuestion?._id;

  useEffect(() => {
    if (!firstQuestion || !autoConfidential || !showConfidentialQuestion)
      return;
    const current = answers?.[firstQuestion?._id];
    if (!current) {
      handleSelectedAnswer(
        { [firstQuestion._id]: { value: 0, label: 'Yes' } },
        firstQuestion.label
      );
    }
  }, [
    answers,
    firstQuestion,
    autoConfidential,
    handleSelectedAnswer,
    showConfidentialQuestion
  ]);

  return (
    <Card name={name} title={reportSectionHeader} showHeader wide>
      {showConfidentialQuestion ? (
        <FieldRenderer
          key={0}
          fieldLabel={firstQuestion.label}
          isRequired={firstQuestion.required}
          name={firstQuestion.label}
          id={firstQuestion._id}
          currentValue={
            !!firstQuestion && !!autoConfidential && !!showConfidentialQuestion
              ? { value: 0, label: 'Yes' }
              : (answers?.[firstQuestion?._id] ?? '')
          }
          onChange={v =>
            handleSelectedAnswer(
              { [firstQuestion?._id]: v },
              firstQuestion.label
            )
          }
          disabled={(alreadyHaveConfidential && disabled) || autoConfidential}
          options={firstQuestion.options}
          type={firstQuestion.type}
          bareValues={false}
          groupId={groupId}
          alphabetize
          missingRequired={missingRequired}
        />
      ) : null}
      {isIncidentBasics && (
        <HierarchySelector
          onGroupChange={v =>
            handleSelectedAnswer({
              locationId: v,
              projectId: ''
            })
          }
          groupId={groupId}
          groupDisabled={disabled}
          onProjectChange={v => handleSelectedAnswer({ projectId: v })}
          projectId={answers?.projectId ?? activeIncident?.projectId}
          projectDisabled={disabled}
          alphabetize
          projectRequired
          allowProjectClear={false}
          groupTouched={missingRequired && !groupId}
          projectTouched={missingRequired && !projectId}
          groupBareValues
        />
      )}
      {fields.map((field, index) => {
        if (field.legacy) return <React.Fragment key={index}></React.Fragment>;
        const answer =
          answers?.[field._id] ?? (field.type === 'multiselect' ? [] : '');

        if (field.label === 'Is this a Confidential Report?') return null;
        return (
          <FieldRenderer
            key={index}
            fieldLabel={field.label}
            isRequired={field.required}
            name={field.label}
            id={field._id}
            showNA={
              field.type === 'signature' ? false : field.showNotApplicable
            }
            isNA={answer?.length === 1 && answer?.[0] === 'Not Applicable'}
            currentValue={answer}
            onChange={v =>
              handleSelectedAnswer({ [field._id]: v }, field.label)
            }
            onNAChange={v =>
              handleSelectedAnswer({
                [field._id]: v
                  ? ['Not Applicable']
                  : field.type === 'multiselect'
                    ? []
                    : ''
              })
            }
            disabled={
              field.label === 'Additional Witnesses of the Incident:'
                ? witnessDisabled
                : disabled ||
                  (isEditing && shouldDisableQuestion(activeIncident, field))
            }
            options={field.options}
            type={field.type}
            bareValues={false}
            groupId={groupId}
            // This message will proabably needed to be worded better
            displayTooltip={
              field.displayTooltip ??
              field.label === 'Additional Witnesses of the Incident:'
            }
            tooltip={
              field.tooltip ??
              'Only Active Users are generated in this list. If a user is not found they will need to be activated on the Employee Page'
            }
            alphabetize={
              isCustomQuestions ? false : (field.alphabetize ?? true)
            }
            firstOptions={field.firstOptions}
            hide={isCustomQuestions ? field.hide && !answer : field.hide}
            missingRequired={missingRequired}
            maxDate={
              field.label === 'When did the incident occur?' ? new Date() : null
            }
          />
        );
      })}
    </Card>
  );
};

export default IncidentSection;
